import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tournamentsApi } from '../api/tournaments';
import { referralsApi } from '../api/referrals';
import { Trophy, Medal, Award, Filter, ChevronLeft, ChevronRight, Users } from 'lucide-react';
import '../styles/football-theme.css';
import '../styles/rankings-gaming.css';
import { useLanguage } from '../i18n/LanguageContext';

const Rankings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tournamentIdParam = searchParams.get('tournamentId');
  const monthlyTournamentIdParam = searchParams.get('monthlyTournamentId');
  const { t, locale } = useLanguage();
  const getMonthName = (month) => new Intl.DateTimeFormat(locale || 'it-IT', { month: 'long' }).format(new Date(2000, month, 1));
  
  const [rankings, setRankings] = useState([]);
  const [isProvisional, setIsProvisional] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [monthlyTournaments, setMonthlyTournaments] = useState([]);
  const [selectedMonthlyTournament, setSelectedMonthlyTournament] = useState(null);
  const [monthlyRankings, setMonthlyRankings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [monthlyCurrentPage, setMonthlyCurrentPage] = useState(0);
  const [totalRankings, setTotalRankings] = useState(0);
  const [monthlyTotalRankings, setMonthlyTotalRankings] = useState(0);
  const [topReferrers, setTopReferrers] = useState([]);
  const [loadingTopRef, setLoadingTopRef] = useState(false);
  const [topRefPeriod, setTopRefPeriod] = useState(''); // '', 'monthly', 'semestral', 'annual'
  const [topRefYear, setTopRefYear] = useState(''); // for monthly/annual
  const [topRefMonth, setTopRefMonth] = useState(''); // for monthly (0-11)
  const [earningsDateRange, setEarningsDateRange] = useState({ minDate: null, maxDate: null });
  const [rankingPreviews, setRankingPreviews] = useState({});
  const [activePreviewKey, setActivePreviewKey] = useState(null);
  const previewRequestsRef = useRef(new Set());
  const ITEMS_PER_PAGE = 6;

  /** Ordine categorie nelle classifiche (stessa gameweek / stessa finestra): basic → plus → … */
  const RARITY_RANKINGS_ORDER = {
    basic: 0,
    plus: 1,
    rare: 2,
    epic: 3,
    unique: 4,
    legend: 5,
    divinity: 6
  };

  const getRaritySortIndex = (tournament) => {
    const raw = tournament?.rarity_restriction;
    if (raw == null || String(raw).trim() === '') return RARITY_RANKINGS_ORDER.basic;
    const r = String(raw).toLowerCase().trim();
    return RARITY_RANKINGS_ORDER[r] ?? 50;
  };

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Build month/year options from actual data range (earningsDateRange) or fallback to now
  const minDate = earningsDateRange?.minDate ? new Date(earningsDateRange.minDate) : now;
  const maxDate = earningsDateRange?.maxDate ? new Date(earningsDateRange.maxDate) : now;
  const dataMinYear = minDate.getFullYear();
  const dataMinMonth = minDate.getMonth();
  const dataMaxYear = maxDate.getFullYear();
  const dataMaxMonth = maxDate.getMonth();

  const monthOptions = [];
  let y = dataMaxYear, m = dataMaxMonth;
  while (y > dataMinYear || (y === dataMinYear && m >= dataMinMonth)) {
    monthOptions.push({ year: y, month: m });
    m--;
    if (m < 0) { m = 11; y--; }
  }

  const yearOptions = [];
  for (let y = dataMaxYear; y >= dataMinYear; y--) {
    yearOptions.push(y);
  }

  useEffect(() => {
    loadTournaments();
    loadMonthlyTournaments();
    loadEarningsRange();
  }, []);

  const loadEarningsRange = async () => {
    try {
      const response = await referralsApi.getEarningsRange();
      if (response?.success && (response.minDate || response.maxDate)) {
        setEarningsDateRange({
          minDate: response.minDate || response.maxDate,
          maxDate: response.maxDate || response.minDate
        });
      }
    } catch (error) {
      console.error('Error loading earnings range:', error);
    }
  };

  useEffect(() => {
    loadTopReferrers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topRefPeriod, topRefYear, topRefMonth]);

  // Load specific gameweek tournament if tournamentId is in URL
  useEffect(() => {
    if (tournamentIdParam && tournaments.length > 0) {
      const tournament = tournaments.find(t => t.id === parseInt(tournamentIdParam, 10));
      if (tournament) {
        setSelectedTournament(tournament);
      } else {
        loadSpecificTournament(parseInt(tournamentIdParam, 10), 'gameweek');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentIdParam, tournaments]);

  useEffect(() => {
    if (monthlyTournamentIdParam && monthlyTournaments.length > 0) {
      const tournament = monthlyTournaments.find((t) => t.id === parseInt(monthlyTournamentIdParam, 10));
      if (tournament) {
        setSelectedMonthlyTournament(tournament);
      } else {
        loadSpecificTournament(parseInt(monthlyTournamentIdParam, 10), 'monthly');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyTournamentIdParam, monthlyTournaments]);

  useEffect(() => {
    if (selectedTournament) {
      setCurrentPage(0);
      loadRankings(selectedTournament.id, false);
    }
  }, [selectedTournament]);

  useEffect(() => {
    if (selectedTournament) {
      loadRankings(selectedTournament.id, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (selectedMonthlyTournament) {
      setMonthlyCurrentPage(0);
      loadRankings(selectedMonthlyTournament.id, true);
    }
  }, [selectedMonthlyTournament]);

  useEffect(() => {
    if (selectedMonthlyTournament) {
      loadRankings(selectedMonthlyTournament.id, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyCurrentPage]);

  useEffect(() => {
    setActivePreviewKey(null);
  }, [selectedTournament?.id, currentPage]);

  const loadSpecificTournament = async (tournamentId, section = 'gameweek') => {
    try {
      const response = await tournamentsApi.getById(tournamentId);
      if (response?.success && response.tournament) {
        const tournament = response.tournament;
        if (section === 'monthly' || tournament.type === 'monthly') {
          setMonthlyTournaments((prev) => (
            prev.find((t) => t.id === tournament.id) ? prev : [...prev, tournament]
          ));
          setSelectedMonthlyTournament(tournament);
        } else {
          setTournaments((prev) => (
            prev.find((t) => t.id === tournament.id) ? prev : [...prev, tournament]
          ));
          setSelectedTournament(tournament);
        }
      }
    } catch (error) {
      console.error('Error loading specific tournament:', error);
    }
  };

  const sortGameweekTournaments = (list) => [...list].sort((a, b) => {
    const gwA = a.gameweek_id != null ? Number(a.gameweek_id) : null;
    const gwB = b.gameweek_id != null ? Number(b.gameweek_id) : null;
    if (Number.isFinite(gwA) && Number.isFinite(gwB) && gwA !== gwB) {
      return gwB - gwA;
    }

    const dateA = new Date(a.start_date || a.gameweek_start_date || 0).getTime();
    const dateB = new Date(b.start_date || b.gameweek_start_date || 0).getTime();
    if (dateB !== dateA) return dateB - dateA;

    const rarityDiff = getRaritySortIndex(a) - getRaritySortIndex(b);
    if (rarityDiff !== 0) return rarityDiff;

    return (Number(a.id) || 0) - (Number(b.id) || 0);
  });

  const loadTournaments = async () => {
    try {
      const gameweekResponse = await tournamentsApi.getActive({ type: 'gameweek' });
      const gameweekList = gameweekResponse?.success && gameweekResponse.tournaments?.length
        ? sortGameweekTournaments(gameweekResponse.tournaments)
        : [];

      setTournaments(gameweekList);

      if (tournamentIdParam) {
        const tournament = gameweekList.find((t) => t.id === parseInt(tournamentIdParam, 10));
        if (tournament) {
          setSelectedTournament(tournament);
        } else {
          loadSpecificTournament(parseInt(tournamentIdParam, 10), 'gameweek');
        }
      } else if (gameweekList.length > 0 && !monthlyTournamentIdParam) {
        setSelectedTournament(gameweekList[0]);
      }
    } catch (error) {
      console.error('Error loading ranking tournaments:', error);
      setTournaments([]);
    }
  };

  const loadMonthlyTournaments = async () => {
    try {
      const response = await tournamentsApi.getPublishedMonthlyRankings();
      const list = response?.success && response.tournaments?.length
        ? response.tournaments
        : [];

      setMonthlyTournaments(list);

      if (monthlyTournamentIdParam) {
        const tournament = list.find((t) => t.id === parseInt(monthlyTournamentIdParam, 10));
        if (tournament) {
          setSelectedMonthlyTournament(tournament);
        } else {
          loadSpecificTournament(parseInt(monthlyTournamentIdParam, 10), 'monthly');
        }
      } else if (list.length > 0) {
        setSelectedMonthlyTournament(list[0]);
      }
    } catch (error) {
      console.error('Error loading monthly ranking tournaments:', error);
      setMonthlyTournaments([]);
    }
  };

  const formatRarityLabel = (rarity) => {
    const normalized = String(rarity || '').toLowerCase().trim();
    if (!normalized || normalized === 'all') {
      return t('rankings.monthlyCategoryGlobal');
    }
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const updateSearchParamsForSection = (section, tournament) => {
    const next = new URLSearchParams(searchParams);
    if (section === 'monthly') {
      if (tournament?.id) {
        next.set('monthlyTournamentId', String(tournament.id));
      } else {
        next.delete('monthlyTournamentId');
      }
    } else if (tournament?.id) {
      next.set('tournamentId', String(tournament.id));
    } else {
      next.delete('tournamentId');
    }
    setSearchParams(next);
  };

  const loadTopReferrers = async () => {
    setLoadingTopRef(true);
    try {
      const period = topRefPeriod || undefined;
      let year = undefined;
      let month = undefined;
      if (period === 'monthly') {
        year = topRefYear !== '' ? parseInt(topRefYear, 10) : currentYear;
        month = topRefMonth !== '' ? parseInt(topRefMonth, 10) : currentMonth;
      } else if (period === 'annual') {
        year = topRefYear !== '' ? parseInt(topRefYear, 10) : currentYear;
      }
      const response = await referralsApi.getTopReferrers(10, period, year, month);
      if (response?.success) {
        setTopReferrers(response.topReferrers || []);
      } else {
        setTopReferrers([]);
      }
    } catch (error) {
      console.error('Error loading top referrers:', error);
      setTopReferrers([]);
    } finally {
      setLoadingTopRef(false);
    }
  };

  const loadRankings = async (tournamentId, isMonthlySection = false) => {
    if (isMonthlySection) {
      setMonthlyLoading(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await tournamentsApi.getRankings(tournamentId, 1000);
      if (response?.success) {
        const allRankings = response.rankings || [];
        const page = isMonthlySection ? monthlyCurrentPage : currentPage;
        const start = page * ITEMS_PER_PAGE;
        const pageSlice = allRankings.slice(start, start + ITEMS_PER_PAGE);
        if (isMonthlySection) {
          setMonthlyTotalRankings(allRankings.length);
          setMonthlyRankings(pageSlice);
        } else {
          setTotalRankings(allRankings.length);
          setRankings(pageSlice);
          setIsProvisional(response.isProvisional === true);
        }
      } else if (response?.error) {
        console.error('Error loading rankings:', response.error);
        if (isMonthlySection) {
          setMonthlyRankings([]);
          setMonthlyTotalRankings(0);
        } else {
          setRankings([]);
          setTotalRankings(0);
          setIsProvisional(false);
        }
      }
    } catch (error) {
      console.error('Error loading rankings:', error);
      if (isMonthlySection) {
        setMonthlyRankings([]);
        setMonthlyTotalRankings(0);
      } else {
        setRankings([]);
        setTotalRankings(0);
        setIsProvisional(false);
      }
    } finally {
      if (isMonthlySection) {
        setMonthlyLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-[#F59E0B]" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-[#94a3b8]" />;
    if (rank === 3) return <Award className="w-6 h-6 text-[#F97316]" />;
    return <span className="rankings-cell-rank font-bold">#{rank}</span>;
  };

  const canShowLineupPreview = selectedTournament?.type === 'gameweek';

  const getPreviewKey = (userId) => `${selectedTournament?.id}-${userId}`;

  const getPlayerDisplayName = (card) => {
    if (!card) return t('common.unknown');
    return (
      card.custom_name ||
      card.customName ||
      card.player?.display_name ||
      card.player?.full_name ||
      card.player?.last_name ||
      t('common.unknown')
    );
  };

  const formatPreviewScore = (value) => {
    const numericValue = Number(value);
    if (value == null || Number.isNaN(numericValue)) {
      return t('common.notAvailable');
    }

    return numericValue.toLocaleString(locale || 'it-IT', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  const getPreviewSections = (team) => {
    if (!team) return [];

    const reserveCards = Object.entries(team.reserves || {})
      .filter(([, card]) => !!card)
      .map(([slot, card]) => ({ ...card, previewSlot: slot }));

    const sections = [
      { title: 'GK', cards: team.goalkeeper ? [team.goalkeeper] : [] },
      { title: 'DEF', cards: team.defenders || [] },
      { title: 'MID', cards: team.midfielders || [] },
      { title: 'ATT', cards: team.attackers || [] }
    ].filter((section) => section.cards.length > 0);

    if (reserveCards.length > 0) {
      sections.push({ title: t('rankings.previewReserves'), cards: reserveCards });
    }

    if (team.legend) {
      sections.push({ title: t('rankings.previewLegend'), cards: [team.legend] });
    }

    return sections;
  };

  const getCardXp = (card) => {
    const rawXp = card?.xp_percentage ?? card?.xpPercentage;
    const xpPercentage = Number(rawXp);
    if (!Number.isNaN(xpPercentage)) {
      return xpPercentage;
    }

    return null;
  };

  const loadRankingPreview = async (userId) => {
    if (!selectedTournament?.id || !userId || !canShowLineupPreview) {
      return;
    }

    const previewKey = getPreviewKey(userId);
    if (rankingPreviews[previewKey]?.loaded || previewRequestsRef.current.has(previewKey)) {
      return;
    }

    previewRequestsRef.current.add(previewKey);
    setRankingPreviews((prev) => ({
      ...prev,
      [previewKey]: {
        ...prev[previewKey],
        loading: true,
        loaded: false,
        error: null
      }
    }));

    try {
      const [formationResponse, scoresResponse] = await Promise.all([
        tournamentsApi.getFormation(selectedTournament.id, userId),
        tournamentsApi.getFormationScores(selectedTournament.id, userId)
      ]);

      const teamPayload = formationResponse?.team?.team || formationResponse?.team || null;

      if (!formationResponse?.success || !teamPayload) {
        throw new Error(t('rankings.previewError'));
      }

      setRankingPreviews((prev) => ({
        ...prev,
        [previewKey]: {
          loading: false,
          loaded: true,
          error: null,
          formation: formationResponse?.team?.formation || null,
          team: teamPayload,
          cardScores: scoresResponse?.success ? (scoresResponse.cardScores || {}) : {},
          cardDetails: scoresResponse?.success ? (scoresResponse.cardDetails || {}) : {}
        }
      }));
    } catch (error) {
      setRankingPreviews((prev) => ({
        ...prev,
        [previewKey]: {
          loading: false,
          loaded: true,
          error: error?.message || t('rankings.previewError')
        }
      }));
    } finally {
      previewRequestsRef.current.delete(previewKey);
    }
  };

  const renderRankingPreview = (previewData, mobile = false) => {
    if (!previewData) {
      return null;
    }

    const sections = getPreviewSections(previewData.team);

    return (
      <div className={`rankings-player-preview-panel${mobile ? ' rankings-player-preview-panel-mobile' : ''}`}>
        <div className="rankings-player-preview-header">
          <span className="rankings-player-preview-title">
            {t('rankings.previewFormationLabel')}: {previewData.formation || '-'}
          </span>
        </div>

        {previewData.loading && (
          <div className="rankings-player-preview-state">{t('common.loading')}</div>
        )}

        {!previewData.loading && previewData.error && (
          <div className="rankings-player-preview-state rankings-player-preview-error">
            {previewData.error}
          </div>
        )}

        {!previewData.loading && !previewData.error && sections.length === 0 && (
          <div className="rankings-player-preview-state">{t('rankings.previewEmpty')}</div>
        )}

        {!previewData.loading && !previewData.error && sections.length > 0 && (
          <div className="rankings-player-preview-sections">
            {sections.map((section) => (
              <div key={section.title} className="rankings-player-preview-section">
                <div className="rankings-player-preview-section-title">{section.title}</div>
                <div className="rankings-player-preview-list">
                  {section.cards.map((card) => {
                    const score = previewData.cardScores?.[card.id] ?? card.score ?? null;
                    return (
                      <div key={`${section.title}-${card.id}`} className="rankings-player-preview-item">
                        <div className="rankings-player-preview-name">
                          {getPlayerDisplayName(card)}
                          {getCardXp(card) != null ? (
                            <span className="rankings-player-preview-level">XP {Number(getCardXp(card)).toFixed(1)}%</span>
                          ) : null}
                          {card.previewSlot ? (
                            <span className="rankings-player-preview-slot">{card.previewSlot}</span>
                          ) : null}
                        </div>
                        <div className="rankings-player-preview-score-wrap">
                          <span className="rankings-player-preview-score">{formatPreviewScore(score)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rankings-container min-h-screen pt-20 pb-8 px-4 sm:px-6 lg:px-8">
      {/* Rankings Background */}
      <div 
        className="rankings-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/rankingbg.png')`
        }}
      ></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <h1 className="text-4xl font-bold text-white mb-8 flex items-center">
          <Trophy className="w-10 h-10 mr-3 text-[#4169E1]" />
          {t('rankings.title')}
        </h1>

        {/* GameWeek rankings */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center">
            <Trophy className="w-7 h-7 mr-2 text-[#4169E1]" />
            {t('rankings.gameweekSectionTitle')}
          </h2>
          <p className="text-white/90 text-sm mb-4">{t('rankings.gameweekSectionDesc')}</p>

        <div className="football-card glass-football p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-[#4169E1]" />
            <h3 className="text-xl font-bold text-white">{t('rankings.selectTitle')}</h3>
          </div>
          <div className="relative">
            <select
              value={selectedTournament?.id || ''}
              onChange={(e) => {
                const tournament = tournaments.find((t) => t.id === parseInt(e.target.value, 10));
                setSelectedTournament(tournament);
                updateSearchParamsForSection('gameweek', tournament);
              }}
              className="w-full px-4 py-3 bg-white border-2 border-[rgba(65,105,225,0.2)] rounded-lg text-[#1e293b] font-semibold focus:outline-none focus:border-[#4169E1] transition-all appearance-none cursor-pointer hover:border-[rgba(65,105,225,0.4)]"
            >
              <option value="" disabled>{t('rankings.selectPlaceholder')}</option>
              {tournaments.map((tournament) => {
                const rarity = tournament.rarity_restriction ? ` - ${tournament.rarity_restriction.charAt(0).toUpperCase() + tournament.rarity_restriction.slice(1)}` : '';
                return (
                  <option key={tournament.id} value={tournament.id} className="bg-white">
                    {tournament.name}{rarity}
                  </option>
                );
              })}
            </select>
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg className="w-5 h-5 text-[#4169E1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          {tournaments.length === 0 && (
            <p className="mt-4 text-sm text-[#64748b] text-center">
              {t('rankings.emptyHint')}
            </p>
          )}
        </div>

        {/* Rankings Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="football-spinner mx-auto"></div>
            <p className="mt-4 text-white/75">{t('rankings.loading')}</p>
          </div>
        ) : (
          <div className="football-card glass-football p-6">
            {selectedTournament && (
              <div className="mb-6">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-2xl font-bold text-[#1e293b]">{selectedTournament.name}</h2>
                  {isProvisional && (
                    <span className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-500/20 text-amber-700 border border-amber-500/40">
                      {t('rankings.provisionalLabel')}
                    </span>
                  )}
                </div>
                {selectedTournament.type !== 'gameweek' && (
                  <p className="text-[#475569] mt-2">{t('rankings.typeLabel')}: {selectedTournament.type}</p>
                )}
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full rankings-table">
                <colgroup>
                  <col className="rankings-col-rank" />
                  <col className="rankings-col-player" />
                  <col className="rankings-col-score" />
                  <col className="rankings-col-prize" />
                </colgroup>
                <thead>
                  <tr className="border-b border-[rgba(65,105,225,0.2)]">
                    <th className="text-left py-3 px-4 text-[#475569]">{t('rankings.tableRank')}</th>
                    <th className="text-left py-3 px-4 text-[#475569]">{t('rankings.tablePlayer')}</th>
                    <th className="text-right py-3 px-4 text-[#475569]">{t('rankings.tableScore')}</th>
                    <th className="text-right py-3 px-4 text-[#475569]">{t('rankings.tablePrize')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rankings.length > 0 ? (
                    rankings.map((entry, index) => {
                      const globalRank = currentPage * ITEMS_PER_PAGE + index + 1;
                      const previewKey = canShowLineupPreview && entry.user_id ? getPreviewKey(entry.user_id) : null;
                      const previewData = previewKey ? rankingPreviews[previewKey] : null;
                      const isPreviewOpen = previewKey && activePreviewKey === previewKey;
                      const username = entry.username || `User #${entry.user_id}`;
                      return (
                        <React.Fragment key={entry.id || `${entry.user_id}-${index}`}>
                          <tr className="border-b border-[rgba(65,105,225,0.1)] hover:bg-[rgba(65,105,225,0.05)]">
                            <td className="py-4 px-4">
                              <div className="flex items-center">
                                {getRankIcon(globalRank)}
                              </div>
                            </td>
                            <td className="py-4 px-4 rankings-player-cell">
                              {previewKey ? (
                                <div className="rankings-player-preview-anchor">
                                  <button
                                    type="button"
                                    className={`rankings-player-trigger rankings-player-trigger-active${isPreviewOpen ? ' rankings-player-trigger-open' : ''}`}
                                    onClick={() => {
                                      setActivePreviewKey((currentKey) => (currentKey === previewKey ? null : previewKey));
                                      loadRankingPreview(entry.user_id);
                                    }}
                                  >
                                    {username}
                                  </button>
                                </div>
                              ) : (
                                <div className="font-semibold text-[#1e293b]">{username}</div>
                              )}
                            </td>
                            <td className="py-4 px-4 text-right">
                              <span className="text-[#4169E1] font-bold">{entry.score || 0}</span>
                            </td>
                            <td className="py-4 px-4 text-right">
                              <span className="text-[#10b981] font-semibold">{entry.prize || '0'} DIOS</span>
                            </td>
                          </tr>

                          {isPreviewOpen && (
                            <tr className="rankings-preview-row">
                              <td colSpan="4" className="py-0 px-4 pb-4">
                                {renderRankingPreview(previewData || { loading: true }, true)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-12 text-center text-[#64748b]">
                        {t('rankings.emptyTable')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {totalRankings > ITEMS_PER_PAGE && (
              <div className="rankings-pagination flex items-center justify-center gap-3 mt-6 flex-wrap">
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className={`rankings-pagination-button rankings-pagination-button-prev ${
                    currentPage === 0
                      ? 'rankings-pagination-button-disabled'
                      : ''
                  }`}
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  {t('common.previous')}
                </button>
                <span className="rankings-pagination-label text-sm font-medium">
                  {t('common.pageOf', { page: currentPage + 1, total: Math.ceil(totalRankings / ITEMS_PER_PAGE) })}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.min(Math.ceil(totalRankings / ITEMS_PER_PAGE) - 1, currentPage + 1))}
                  disabled={currentPage >= Math.ceil(totalRankings / ITEMS_PER_PAGE) - 1}
                  className={`rankings-pagination-button rankings-pagination-button-next ${
                    currentPage >= Math.ceil(totalRankings / ITEMS_PER_PAGE) - 1
                      ? 'rankings-pagination-button-disabled'
                      : ''
                  }`}
                >
                  {t('common.next')}
                  <ChevronRight className="w-5 h-5 ml-1" />
                </button>
              </div>
            )}
          </div>
        )}
        </section>

        {/* Monthly rankings (published by admin, separate section) */}
        <section className="mb-10 rankings-monthly-section">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center">
            <Medal className="w-7 h-7 mr-2 text-[#10b981]" />
            {t('rankings.monthlySectionTitle')}
          </h2>
          <p className="text-white/90 text-sm mb-4">{t('rankings.monthlySectionDesc')}</p>

          <div className="football-card glass-football p-6 mb-6">
            <div className="flex items-center space-x-2 mb-4">
              <Filter className="w-5 h-5 text-[#10b981]" />
              <h3 className="text-xl font-bold text-white">{t('rankings.monthlySelectTitle')}</h3>
            </div>
            <div className="relative">
              <select
                value={selectedMonthlyTournament?.id || ''}
                onChange={(e) => {
                  const tournament = monthlyTournaments.find((t) => t.id === parseInt(e.target.value, 10));
                  setSelectedMonthlyTournament(tournament);
                  updateSearchParamsForSection('monthly', tournament);
                }}
                className="w-full px-4 py-3 bg-white border-2 border-[rgba(16,185,129,0.25)] rounded-lg text-[#1e293b] font-semibold focus:outline-none focus:border-[#10b981] transition-all appearance-none cursor-pointer hover:border-[rgba(16,185,129,0.4)]"
              >
                <option value="" disabled>{t('rankings.monthlySelectPlaceholder')}</option>
                {monthlyTournaments.map((tournament) => (
                  <option key={tournament.id} value={tournament.id} className="bg-white">
                    {tournament.name.replace(' [PUBBLICATA]', '')}
                  </option>
                ))}
              </select>
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg className="w-5 h-5 text-[#10b981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {monthlyTournaments.length === 0 && (
              <p className="mt-4 text-sm text-[#64748b] text-center">
                {t('rankings.monthlyEmptyHint')}
              </p>
            )}
          </div>

          {monthlyLoading ? (
            <div className="text-center py-12">
              <div className="football-spinner mx-auto"></div>
              <p className="mt-4 text-white/75">{t('rankings.loading')}</p>
            </div>
          ) : (
            <div className="football-card glass-football p-6 border-2 border-[rgba(16,185,129,0.15)]">
              {selectedMonthlyTournament && (
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-[#1e293b]">
                    {selectedMonthlyTournament.name.replace(' [PUBBLICATA]', '')}
                  </h3>
                  <p className="text-[#475569] mt-2">
                    {t('rankings.monthlyCategoryLabel')}: {formatRarityLabel(selectedMonthlyTournament.rarity_restriction)}
                    {selectedMonthlyTournament.prize_pool > 0 && (
                      <span className="ml-3 text-amber-600 font-semibold">
                        · {t('rankings.monthlyPrizePoolLabel')}: {Number(selectedMonthlyTournament.prize_pool).toLocaleString(locale || 'it-IT')} DIOS
                      </span>
                    )}
                  </p>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full rankings-table">
                  <colgroup>
                    <col className="rankings-col-rank" />
                    <col className="rankings-col-player" />
                    <col className="rankings-col-score" />
                    <col className="rankings-col-prize" />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-[rgba(16,185,129,0.2)]">
                      <th className="text-left py-3 px-4 text-[#475569]">{t('rankings.tableRank')}</th>
                      <th className="text-left py-3 px-4 text-[#475569]">{t('rankings.tablePlayer')}</th>
                      <th className="text-right py-3 px-4 text-[#475569]">{t('rankings.tableScore')}</th>
                      <th className="text-right py-3 px-4 text-[#475569]">{t('rankings.tablePrize')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyRankings.length > 0 ? (
                      monthlyRankings.map((entry, index) => {
                        const globalRank = monthlyCurrentPage * ITEMS_PER_PAGE + index + 1;
                        const username = entry.username || `User #${entry.user_id}`;
                        return (
                          <tr
                            key={entry.id || `${entry.user_id}-${index}`}
                            className="border-b border-[rgba(16,185,129,0.1)] hover:bg-[rgba(16,185,129,0.05)]"
                          >
                            <td className="py-4 px-4">
                              <div className="flex items-center">{getRankIcon(globalRank)}</div>
                            </td>
                            <td className="py-4 px-4">
                              <div className="font-semibold text-[#1e293b]">{username}</div>
                            </td>
                            <td className="py-4 px-4 text-right">
                              <span className="text-[#4169E1] font-bold">{entry.score || 0}</span>
                            </td>
                            <td className="py-4 px-4 text-right">
                              <span className="text-[#10b981] font-semibold">{entry.prize || '0'} DIOS</span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="py-12 text-center text-[#64748b]">
                          {t('rankings.monthlyEmptyTable')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {monthlyTotalRankings > ITEMS_PER_PAGE && (
                <div className="rankings-pagination flex items-center justify-center gap-3 mt-6 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setMonthlyCurrentPage(Math.max(0, monthlyCurrentPage - 1))}
                    disabled={monthlyCurrentPage === 0}
                    className={`rankings-pagination-button rankings-pagination-button-prev ${
                      monthlyCurrentPage === 0 ? 'rankings-pagination-button-disabled' : ''
                    }`}
                  >
                    <ChevronLeft className="w-5 h-5 mr-1" />
                    {t('common.previous')}
                  </button>
                  <span className="rankings-pagination-label text-sm font-medium">
                    {t('common.pageOf', {
                      page: monthlyCurrentPage + 1,
                      total: Math.ceil(monthlyTotalRankings / ITEMS_PER_PAGE),
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => setMonthlyCurrentPage(
                      Math.min(Math.ceil(monthlyTotalRankings / ITEMS_PER_PAGE) - 1, monthlyCurrentPage + 1)
                    )}
                    disabled={monthlyCurrentPage >= Math.ceil(monthlyTotalRankings / ITEMS_PER_PAGE) - 1}
                    className={`rankings-pagination-button rankings-pagination-button-next ${
                      monthlyCurrentPage >= Math.ceil(monthlyTotalRankings / ITEMS_PER_PAGE) - 1
                        ? 'rankings-pagination-button-disabled'
                        : ''
                    }`}
                  >
                    {t('common.next')}
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Top Referrers - same table structure as /admin/affiliations */}
        <div className="football-card glass-football p-6 mt-6 rankings-top-referrers">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-[#4169E1]" />
            <h2 className="text-xl font-bold text-[#1e293b]">{t('rankings.topRefTitle')}</h2>
          </div>
          <p className="rankings-top-desc text-[#475569] text-sm mb-6">{t('rankings.topRefDesc')}</p>
          <div className="mb-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-2 uppercase tracking-wider">
                {t('rankings.topRefPeriodLabel')}
              </label>
              <div className="relative">
                <select
                  value={topRefPeriod}
                  onChange={(e) => {
                    setTopRefPeriod(e.target.value);
                    if (e.target.value === 'monthly') {
                      setTopRefYear(currentYear);
                      setTopRefMonth(currentMonth);
                    } else if (e.target.value === 'annual') {
                      setTopRefYear(currentYear);
                      setTopRefMonth('');
                    } else {
                      setTopRefYear('');
                      setTopRefMonth('');
                    }
                  }}
                  className="w-full px-4 py-3 bg-white border-2 border-[rgba(65,105,225,0.2)] rounded-lg text-[#1e293b] font-semibold focus:outline-none focus:border-[#4169E1] transition-all appearance-none cursor-pointer hover:border-[rgba(65,105,225,0.4)]"
                >
                  <option value="">{t('rankings.topRefPeriodAll')}</option>
                  <option value="monthly">{t('rankings.topRefPeriodMonthly')}</option>
                  <option value="semestral">{t('rankings.topRefPeriodSemestral')}</option>
                  <option value="annual">{t('rankings.topRefPeriodAnnual')}</option>
                </select>
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-[#4169E1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            {topRefPeriod === 'monthly' && (
              <div>
                <label className="block text-sm font-semibold text-[#475569] mb-2 uppercase tracking-wider">
                  {t('rankings.topRefSelectMonth')}
                </label>
                <div className="relative">
                  <select
                    value={topRefYear !== '' && topRefMonth !== '' ? `${topRefYear}-${topRefMonth}` : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) {
                        const [y, m] = v.split('-').map(Number);
                        setTopRefYear(y);
                        setTopRefMonth(m);
                      }
                    }}
                    className="w-full px-4 py-3 bg-white border-2 border-[rgba(65,105,225,0.2)] rounded-lg text-[#1e293b] font-semibold focus:outline-none focus:border-[#4169E1] transition-all appearance-none cursor-pointer hover:border-[rgba(65,105,225,0.4)]"
                  >
                    <option value="">{t('rankings.topRefSelectMonth')}</option>
                    {monthOptions.map(({ year, month }) => (
                      <option key={`${year}-${month}`} value={`${year}-${month}`}>
                        {getMonthName(month)} {year}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-5 h-5 text-[#4169E1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            )}
            {topRefPeriod === 'annual' && (
              <div>
                <label className="block text-sm font-semibold text-[#475569] mb-2 uppercase tracking-wider">
                  {t('rankings.topRefSelectYear')}
                </label>
                <div className="relative">
                  <select
                    value={topRefYear}
                    onChange={(e) => setTopRefYear(e.target.value)}
                    className="w-full px-4 py-3 bg-white border-2 border-[rgba(65,105,225,0.2)] rounded-lg text-[#1e293b] font-semibold focus:outline-none focus:border-[#4169E1] transition-all appearance-none cursor-pointer hover:border-[rgba(65,105,225,0.4)]"
                  >
                    <option value="">{t('rankings.topRefSelectYear')}</option>
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-5 h-5 text-[#4169E1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            )}
          </div>
          {loadingTopRef ? (
            <div className="text-center py-8">
              <div className="football-spinner mx-auto w-8 h-8"></div>
              <p className="mt-2 text-sm text-[#64748b]">{t('rankings.loading')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="rankings-top-table w-full">
                <colgroup>
                  <col style={{ width: '5rem' }} />
                  <col />
                  <col style={{ width: '9rem' }} />
                  <col style={{ width: '12rem' }} />
                </colgroup>
                <thead>
                  <tr className="border-b border-[rgba(65,105,225,0.2)]">
                    <th className="py-3 px-4">{t('rankings.tableRank')}</th>
                    <th className="py-3 px-4">{t('rankings.tablePlayer')}</th>
                    <th className="py-3 px-4">{t('rankings.topRefColReferrals')}</th>
                    <th className="py-3 px-4">{t('rankings.topRefColEarnings')}</th>
                  </tr>
                </thead>
                <tbody>
                  {topReferrers.length > 0 ? (
                    topReferrers.map((ref, index) => (
                      <tr key={ref.referrerId} className="border-b border-[rgba(65,105,225,0.1)] hover:bg-[rgba(65,105,225,0.05)]">
                        <td className="py-4 px-4">
                          <div className="flex justify-center items-center">
                            {getRankIcon(index + 1)}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="font-semibold text-[#1e293b]">{ref.username || `User #${ref.referrerId}`}</div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="rankings-cell-referrals font-bold">{ref.totalReferrals ?? 0}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="rankings-cell-earnings font-semibold">
                            {parseFloat(ref.totalEarnings || 0).toLocaleString(locale || 'it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DIOS
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-8 text-center text-[#64748b]">
                        {t('rankings.topRefEmpty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Rankings;

